import React, { Fragment, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.css';
import '../../scss/app.scss';
import Routes from './Routes';
import store from './store';
import ScrollToTop from './ScrollToTop';
import {getBearer, getOrganization, getAuthUser} from './auth';
import axios from 'axios'
import {setOrganization} from '@/redux/actions/organizationActions';
import {setAuthUser} from '@/redux/actions/userActions';
import {setSentryUserContext} from '@/sentry/userContext';
import {sendFlashMessage, FlashMessage} from "@/shared/components/flash";
import {ConfigProvider} from "antd";

require('dotenv').config()
axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL + '/api/v1';  
axios.defaults.headers.common['Authorization'] = getBearer();
axios.defaults.headers.post['Content-Type'] = 'application/json';

axios.interceptors.request.use(request => {
    // console.log(request);
    // Edit request config
    return request;
}, error => {
    // console.log(error);
    return Promise.reject(error);
});

axios.interceptors.response.use(
  response => response,
  error => {
    const status = error.response?.status;
    const data = error.response?.data || {};

    //HANDLE 2FA
    if (status === 403 && error.response?.data?.message === "Code is required") {
      return Promise.reject(error);
    }

    //HANDLE "one login attempt remaining" warning - let LogInForm show its own modal
    if (status === 422 && data?.remaining_attempts === 1) {
      return Promise.reject(error);
    }

    //Rate limit handler
    if (status === 429) {
      const { message } = data;

      if (message) {
        store.dispatch(sendFlashMessage(message, "alert-danger"));
      }

      return Promise.resolve({
        data,
        status,
        rateLimited: true,
        handled: true,
      });
    }

    // A caller that shows its own error message opts out of the global flash.
    if (status >= 400 && error.config?.suppressErrorFlash) {
      return Promise.resolve({ data, status, handled: true });
    }

    // ALL API errors (400–599)
    if (status >= 400) {
      let message = null;
      let isValidationError = false;

      // flat error list, e.g. { errors: ["Some message"] } - most
      // exception-style API errors (not per-field validation) shape their
      // response this way. Checked before the plain-object branch below,
      // since Array.isArray(data.errors) is also typeof "object" and would
      // otherwise fall into Object.values(...)[0][0], which indexes into
      // the message *string* and yields just its first character.
      if (Array.isArray(data.errors)) {
        message = data.errors[0];
      }

      // validation error (object of field -> [messages])
      else if (data.errors && typeof data.errors === "object") {
        message = Object.values(data.errors)?.[0]?.[0];
        isValidationError = true;
      }

      //string error (exception)
      else if (typeof data.errors === "string") {
        message = null;
      }

      //fallback
      message =
        message ||
        data.message ||
        "Oops, something went wrong. Please contact support.";

      const supportUrl = data.support_url;

      store.dispatch(
        sendFlashMessage(
          <>
            {message}{" "}
            {!isValidationError &&  supportUrl && (
              <a href={supportUrl} target="_blank" rel="noreferrer">
                here
              </a>
            )}
          </>,
          "alert-danger",
        ),
      );

      return Promise.resolve({
        data,
        status,
        handled: true,
      });
    }

    // Network / unknown errors
    const fallbackMessage =
      error.request
        ? "Network error. Please check your connection."
        : "Unexpected error occurred.";

    store.dispatch(sendFlashMessage(fallbackMessage, "alert-danger"));

    return Promise.resolve({
      data: null,
      status: 500,
      handled: true,
    });

    // default
    //return Promise.reject(error);
  }
);



const App = () => {

  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    window.addEventListener('load', () => {
      setIsLoading(false);
      setTimeout(() => setIsLoaded(true), 500)
      setAuthOrganization()
      checkFlashMessage()
    });
  }, []);

  const setAuthOrganization = () => {
    // console.log("getOrganization()")
    // console.log(getOrganization())
    store.dispatch(setOrganization(getOrganization()))
    // store.dispatch(setSuOrganization(getSuOrganization()))
    const authUser = getAuthUser()
    store.dispatch(setAuthUser(authUser))
    setSentryUserContext(authUser)
  }

  const checkFlashMessage = () => {
    const params = new URLSearchParams(window.location.search)
    let message = params.get('message')
    if( message ) {
      store.dispatch(sendFlashMessage(message, 'alert-success'))
    }
  }

  return (
    <Router>
      <ScrollToTop>
        <Fragment>
          {!isLoaded && (
            <div className={`load${isLoading ? '' : ' loaded'}`}>
              <div className="load__icon-wrap">
                <svg className="load__icon">
                  <path fill="#4ce1b6" d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
                </svg>
              </div>
            </div>
          )}
          <div>
            <Routes />
            <FlashMessage />
          </div>
        </Fragment>
      </ScrollToTop>
    </Router>
  )
}

const AppProvider = () => {
    return (
        <Provider store={store}>
            <ConfigProvider
                theme={{
                    token: {
                        colorPrimary: '#4ce1b6',
                        borderRadius: 2,
                        colorBgContainer: '#ffffff',
                    },
                    components: {
                        hoverBorderColor: '#4ce1b6',
                        Button: {
                            colorPrimary: '#70bbfd',
                            algorithm: true,
                        },
                        Table: {
                            fontSize: '13px',
                            cellPadding: '10px',
                            cellPaddingBlock: 4,
                            stickyScrollBarBg: '#4ce1b6',
                        },
                    },
                }}
            >
                <App/>
            </ConfigProvider>
        </Provider>
  )
}

export default AppProvider
